import React, { useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Button, notification, Slider } from 'antd';
import DropdownSelect from '../../DropdownSelect/DropdownSelect';
import RowAnswer from '../../RowAnswer/RowAnswer';
import { t } from '../../../i18n/i18n';
import { AnswerTypeEnum, PrefilterNumberConditions } from '../../../constants/enums';
import AnswerTypeButton from '../../AddQuestions/AnswerTypeButton/AnswerTypeButton';
import MultipleChoice from '../../../assets/additionalQuestions/multipleChoice';
import TextAnswer from '../../../assets/additionalQuestions/textAnswer';
import WriteAnswer from '../../../assets/additionalQuestions/writeAnswer';
import InfoTooltip from '../../InfoTooltip/InfoTooltip';
import { isNullOrUndefined } from '../../../utils/typesUtils';
import LocationAnswer from '../../../assets/additionalQuestions/location';
import AddressSearch from '../../Map/MapContainer';
import DeleteForever from '../../../assets/screening/DeleteForever';
import { mapLocationToOptions, mapOptionsToLocations } from '../../../utils/tests';

const emptyOption = {
  text: '',
  correct: false,
  formula: '',
};

const emptyQuestion = {
  text: '',
  title: '',
  answerType: 'RADIO',
  type: 'PRE_FILTER',
  weight: 1,
  hasGlobalScore: true,
  options: [emptyOption, emptyOption],
};

export default function PrefilterQuestionRow({
  handleSubmit,
  editQuestion,
  closeEditor,
  handleEdit,
  handleCurrentTypeQuestion,
}) {
  const [question, setQuestion] = useState(emptyQuestion);
  const [answerType, setAnswerType] = useState(emptyQuestion.answerType);
  const [options, setOptions] = useState(question.options);
  const [disabledAdd, setDisabledAdd] = useState(false);
  const [customValidator, setCustomValidator] = useState(false);
  const [enabledEditRegex, setEnabledEditRegex] = useState(false);
  const [regexError, setRegexError] = useState(false);
  const customRegexId = 'CUSTOM';

  const handleChange = (field, index, value) => {
    switch (field) {
      case 'question':
        setQuestion({
          ...question,
          text: value,
        });
        break;
      case 'answerType':
        setQuestion({
          ...emptyQuestion,
          answerType: value,
        });
        setOptions(emptyQuestion.options);
        break;
      case 'text':
      case 'condition':
      case 'subQuestionText':
      case 'correct': {
        const copyOptions = [...question.options];
        if (field === 'correct' && value) {
          copyOptions.forEach((item) => {
            item.weight = 0;
            item.correct = false;
          });
        }
        copyOptions[index] = {
          ...copyOptions[index],
          [field]: value,
          weight: field === 'correct' && value ? 1 : 0,
          formula: '',
        };
        setQuestion({
          ...question,
          options: copyOptions,
        });
        setOptions(copyOptions);
        break;
      }
      case 'maxChar': {
        setQuestion({
          ...question,
          [field]: value,
        });
        break;
      }
      case 'validatorRegex': {
        const founded = commonValidators.find((v) => v.regex === value);
        const id = founded?.id ?? customRegexId;
        const oldRegex =
          enabledEditRegex || !question?.validatorRegex
            ? value ?? founded?.regex
            : question?.validatorRegex;
        if (!enabledEditRegex) setEnabledEditRegex(true);
        setQuestion({
          ...question,
          validatorType: id,
          [field]: oldRegex,
        });
        break;
      }
      case 'childrenQuestion': {
        const copyOptions = [...question.options];
        const shouldHidden = copyOptions[index]?.hasSubQuestion;
        copyOptions[index] = {
          ...copyOptions[index],
          hasSubQuestion: !shouldHidden,
          ...(shouldHidden ? { subQuestionText: null } : {}),
        };
        setQuestion({
          ...question,
          options: copyOptions,
        });
        setOptions(copyOptions);
        break;
      }
      case 'locations': {
        let copyLocations = question?.locations ?? [];
        if (index >= 0) {
          copyLocations = question?.locations?.filter(
            (l) => l.formatedAddres !== question.locations[index]?.formatedAddres
          );
        } else {
          if (question?.locations?.some((l) => l.formatedAddres === value?.formatedAddres)) {
            notification.open({
              message: t('ADRESS_ALREADY_SET'),
              type: 'error',
            });
            return;
          }
          copyLocations.push(value);
        }
        const options = mapLocationToOptions(copyLocations);
        setQuestion({
          ...question,
          ...options,
          locations: copyLocations,
        });
        break;
      }
      case 'locationArea': {
        const copyLocations = [...question?.locations];
        copyLocations[index] = {
          ...copyLocations[index],
          color: '#4987be',
          radiusInKm: value,
        };
        const options = mapLocationToOptions(copyLocations);
        setQuestion({
          ...question,
          ...options,
          locations: copyLocations,
        });
        break;
      }
      default:
        break;
    }
  };

  const addOption = () => {
    if (
      (options.length < 4 && answerType === AnswerTypeEnum.RADIO) ||
      (options.length < 10 && answerType === AnswerTypeEnum.SELECT)
    ) {
      const copyOptions = [
        ...options,
        {
          text: '',
          fieldType: '',
          options: [
            {
              text: '',
              correct: '',
              formula: '',
            },
          ],
        },
      ];
      setQuestion({
        ...question,
        options: copyOptions,
      });
      setOptions(copyOptions);
    } else {
      notification.open({ message: t('CREATE_PRE_FILTER_MAX_OPTIONS_REACHED'), type: 'warning' });
    }
  };

  const removeOption = (index) => {
    if (options.length > 1) {
      const newOptions = options.filter((_, i) => i !== index);
      question.options = newOptions;
      setOptions(newOptions);
    }
  };

  const fieldTypes = [
    {
      id: AnswerTypeEnum.RADIO,
      name: t('CREATE_SCREENING_YOUR_QUESTIONS_CHECK'),
      show: true,
      img: <MultipleChoice />,
      isPremiumFeature: false,
    },
    {
      id: AnswerTypeEnum.TEXT,
      name: t('CREATE_PRE_FILTER_QUESTION_FIELD_TYPE_NRO'),
      show: true,
      img: <TextAnswer />,
      isPremiumFeature: false,
    },
    {
      id: AnswerTypeEnum.LOCATIONS,
      name: t('Mapa'),
      show: true,
      img: <LocationAnswer />,
      isPremiumFeature: false,
    },
  ];

  const otherFieldTypes = [
    {
      id: AnswerTypeEnum.WRITE,
      name: t('CREATE_PREFILTER_QUESTIONS_WRITE'),
      show: true,
      img: <WriteAnswer />,
      isPremiumFeature: false,
      greenColor: true,
    },
    {
      id: AnswerTypeEnum.SELECT,
      name: t('CREATE_PREFILTER_QUESTIONS_SELECT'),
      show: true,
      img: <WriteAnswer />,
      isPremiumFeature: false,
      greenColor: true,
    },
  ];

  const numberConditionals = [
    { id: PrefilterNumberConditions.EQUAL, name: t('CREATE_PRE_FILTER_NUMERIC_LIMIT_EQUALS') },
    { id: PrefilterNumberConditions.LESS, name: t('CREATE_PRE_FILTER_NUMERIC_LIMIT_LESS') },
    { id: PrefilterNumberConditions.GREATER, name: t('CREATE_PRE_FILTER_NUMERIC_LIMIT_MORE') },
  ];

  const commonValidators = [
    { id: 'TEXT', name: t('CREATE_PREFILTER_QUESTIONS_REGEX_TEXT'), regex: '.*' },
    { id: 'NUMBER', name: t('CREATE_PREFILTER_QUESTIONS_REGEX_NUMBER'), regex: '^\\d*$' },
    { id: 'EMAIL', name: t('CREATE_PREFILTER_QUESTIONS_REGEX_EMAIL'), regex: '^\\S+@\\S+\\.\\S+$' },
    { id: customRegexId, name: t('CREATE_PREFILTER_QUESTIONS_REGEX_CUSTOM'), regex: '' },
    {
      id: 'URL',
      name: t('CREATE_PREFILTER_QUESTIONS_REGEX_URL'),
      regex: '^(https?:\\/\\/)?([\\w-]+\\.)+[\\w-]+(\\/[\\w-]*)*\\/?$',
    },
  ];

  const handleLoadQuestion = (question) => {
    setQuestion(question);
    setOptions(question.options);
    setAnswerType(question.answerType);
  };

  const handleAddQuestion = () => {
    handleSubmit(question);
    handleLoadQuestion(emptyQuestion);
  };

  const handleEditQuestion = () => {
    handleEdit(question);
    handleLoadQuestion(emptyQuestion);
  };

  useEffect(() => {
    const disabledForNumber =
      answerType === AnswerTypeEnum.TEXT &&
      !Number(question.options[0]?.text ?? question?.acceptedOptions[0]);
    const disableForEmptyOption =
      answerType === AnswerTypeEnum.RADIO && options.some((opt) => opt.text === '');
    const disableForNotCorrectOption =
      answerType === AnswerTypeEnum.RADIO && !options.some((opt) => opt.correct === true);
    const disableForNotSelectedCriteria =
      answerType === AnswerTypeEnum.TEXT &&
      !numberConditionals.some((cond) => cond.id === question?.options[0]?.condition);
    const disableForEmptyQuestion = question.text === '' || question.text?.trim() === '';
    const disabledWrite =
      answerType === AnswerTypeEnum.WRITE &&
      (!commonValidators?.some((v) => v.id === question?.validatorType) ||
        isNullOrUndefined(question?.maxChar));
    const disableSelect =
      answerType === AnswerTypeEnum.SELECT &&
      question.options.some((o) => o?.text?.trim() === '' || o?.subQuestionText?.trim() === '');
    const disableMap =
      answerType === AnswerTypeEnum.LOCATIONS &&
      question?.options?.some((opt) => opt.text === '' || opt.weight <= 0);

    setDisabledAdd(
      disableForEmptyOption ||
        disableForNotCorrectOption ||
        disabledForNumber ||
        disableForEmptyQuestion ||
        disableForNotSelectedCriteria ||
        disabledWrite ||
        disableSelect ||
        disableMap
    );
  }, [answerType, question, options]);

  useEffect(() => {
    if (editQuestion) {
      let optionsFormatted = editQuestion?.options;
      if (
        editQuestion?.testId &&
        editQuestion.answerType === AnswerTypeEnum.TEXT &&
        editQuestion?.acceptedOptions
      ) {
        optionsFormatted = editQuestion?.acceptedOptions.map((opt) => ({
          text: opt.options[0],
          condition: editQuestion.writeResponse,
        }));
        editQuestion.options = optionsFormatted;
      } else if (
        editQuestion?.testId &&
        editQuestion.answerType === AnswerTypeEnum.RADIO &&
        editQuestion?.acceptedOptions
      ) {
        optionsFormatted = editQuestion?.acceptedOptions.map((opt) => {
          const option = editQuestion?.options.find((o) => o.id === opt.options[0]);
          return {
            ...option,
            correct: opt.weight === 1,
          };
        });
      }
      if (
        editQuestion?.answerType === AnswerTypeEnum.WRITE &&
        editQuestion?.validatorType === customRegexId
      ) {
        setCustomValidator(true);
      }
      if (editQuestion?.answerType === AnswerTypeEnum.LOCATIONS && editQuestion?.options) {
        const options = mapOptionsToLocations(editQuestion?.options, editQuestion?.acceptedOptions);
        editQuestion.locations = options;
      }
      handleLoadQuestion({ ...editQuestion, options: optionsFormatted });
    }
  }, [editQuestion]);

  useEffect(() => {
    if (!editQuestion) {
      handleCurrentTypeQuestion(AnswerTypeEnum.RADIO);
    }
  }, []);

  return (
    <div className="container">
      <div className="w-100 col justify-content-center mx-auto border-grey-2 my-2 m-3 p-sm-4 gap-1">
        <div className="d-flex flex-column">
          <div className="d-flex justify-content-between w-100 mt-2">
            <Button
              onClick={(e) => {
                e.preventDefault();
                closeEditor(true);
                handleLoadQuestion(emptyQuestion);
              }}
              icon={<CloseOutlined />}
              type="link"
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                border: 'none',
              }}
            />
          </div>
        </div>
        <div className="d-flex w-100 h-95 mt-1 border-3">
          <div className="w-25 column justify-content-center column align-items-end mx-2">
            <span className="d-flex mb-2 font-bold text-muted font-weight-bold text-xs">
              {t('CREATE_PRE_FILTER_QUESTION_TEXT_TITLE')}
            </span>
            {fieldTypes.map((button, index) => (
              <div
                title={t('CREATE_PRE_FILTER_QUESTION_OPTIONS_TITLE')}
                onClick={() => {
                  handleChange('answerType', 0, button?.id);
                  setAnswerType(button?.id);
                }}
              >
                <AnswerTypeButton button={button} index={index} selected={answerType} />
              </div>
            ))}
            <span className="d-flex mb-2 font-bold text-muted font-weight-bold text-xs">
              {t('CREATE_PRE_FILTER_QUESTION_INFO_TITLE')}
            </span>
            {otherFieldTypes.map((button, index) => (
              <div
                title={t('CREATE_PRE_FILTER_QUESTION_INFO_TITLE')}
                onClick={() => {
                  handleChange('answerType', 0, button?.id);
                  setAnswerType(button?.id);
                }}
              >
                <AnswerTypeButton button={button} index={index} selected={answerType} />
              </div>
            ))}
          </div>
          <div className="w-75 column justify-content-center column align-items-end">
            <div className="row mx-2">
              <textarea
                type="text"
                onChange={(e) => handleChange('question', 0, e?.target?.value)}
                name="text"
                value={question.text}
                className="w-100 mt-3 py-2 px-3  border-thin-blue-principal unselected-border outline-none text-s min-height-add-question"
                rows="3"
                maxLength={5000}
                placeholder={t('CREATE_SCREENING_YOUR_QUESTIONS_MULTIPLE_CHOICE_PLACEHOLDER')}
                data-testid="question-mc-add-question-textarea"
              />
            </div>
            <div className="row my-2">
              {answerType === AnswerTypeEnum.RADIO && (
                <div className="w-100 mx-3">
                  <span className=" font-bold text-muted font-weight-bold text-xs m-3">
                    {t('CREATE_PRE_FILTER_QUESTIONS_MC_TITLE')}
                  </span>
                  {options.map((option, index) => (
                    <div key={option.id} id={option.id} className="w-100 mx-2">
                      <RowAnswer
                        {...option}
                        index={index}
                        key={option.id}
                        onChange={(field, value) => handleChange(field, index, value)}
                        eachScore={false}
                        correct={option.correct}
                        onRemove={() => removeOption(index)}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    className=" bg-transparent border-0 text-xs text-blue-principal font-weight-600 mt-3 ml-3"
                    onClick={addOption}
                  >
                    {t('CREATE_PRE_FILTER_QUESTIONS_MC_ADD_OPTION')}
                  </button>
                </div>
              )}
              {answerType === AnswerTypeEnum.SELECT && (
                <div className="w-100 mx-3  overflow-y-scroll">
                  <div className="w-100 mb-2 ml-4">{t('TEXT_PREFILTER_TYPE_RESPONSE_DETAIL')}</div>
                  <div className="d-flex">
                    <span className=" font-bold text-muted font-weight-bold text-xs m-3">
                      {t('CREATE_PRE_FILTER_QUESTIONS_SELECT_TITLE')}
                    </span>
                    <InfoTooltip
                      placement="right"
                      className="mt-3"
                      content={
                        <div className="d-flex flex-column p-2">
                          <span className=" text-xs mb-2">
                            {t('CREATE_PRE_FILTER_QUESTIONS_SELECT_TOOLTIP')}
                          </span>
                        </div>
                      }
                    />
                  </div>
                  <div className="options-prefilter-max-height flex-grow-1">
                    {options.map((option, index) => (
                      <div key={option.id} id={option.id} className="w-100 mx-2">
                        <RowAnswer
                          {...option}
                          index={index}
                          key={option.id}
                          onChange={(field, value) => handleChange(field, index, value)}
                          eachScore={false}
                          correct={option.correct}
                          onRemove={() => removeOption(index)}
                          withChildrenRow
                        />
                        {question.options[index]?.hasSubQuestion && (
                          <input
                            type="text"
                            value={question.options[index]?.subQuestionText}
                            onChange={(e) => {
                              handleChange('subQuestionText', index, e.target.value);
                            }}
                            placeholder={t(`PREFILTER_SUBQUESTION_PLACEHOLDER_QUESTION`)?.replace(
                              '{{QUESTION}}',
                              question.options[index]?.text
                            )}
                            className="w-85 mt-2 py-2 ml-4 align-content-center px-3 border-thin-blue-principal outline-none text-xs"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    className=" bg-transparent border-0 text-xs text-blue-principal font-weight-600 mt-3 ml-3"
                    onClick={addOption}
                  >
                    {t('CREATE_PRE_FILTER_QUESTIONS_MC_ADD_OPTION')}
                  </button>
                </div>
              )}

              {answerType === AnswerTypeEnum.TEXT && (
                <div className="ml-3">
                  <span className=" font-bold text-muted font-weight-bold text-xs m-3">
                    {t('CREATE_PRE_FILTER_QUESTIONS_NRO_CONDITION')}
                  </span>
                  <div className="w-100 ml-3 row justify-content-between">
                    <span className="align-items-start mt-2 mb-3">
                      <DropdownSelect
                        selectedPlaceholder
                        alignEnd="justify-content-center py-1"
                        placeholder={t('CREATE_PRE_FILTER_QUESTION_FIELD_TYPE_TITLE')}
                        setState={(e) => handleChange('condition', 0, e.id)}
                        selected={question?.options[0]?.condition}
                        options={numberConditionals?.map((item) => ({
                          id: item.id,
                          name: item.name,
                        }))}
                      />
                    </span>
                    <input
                      type="number"
                      min={0}
                      pattern="\d*"
                      className="w-80 mt-3 mb-5 ml-3 py-2 px-3  border-thin-blue-principal outline-none text-xs"
                      value={question.options[0]?.text}
                      onChange={(e) => handleChange('text', 0, e.target.value)}
                    />
                  </div>
                </div>
              )}
              {answerType === AnswerTypeEnum.WRITE && (
                <div className="ml-5 w-90">
                  <div className="w-100 mb-2">{t('TEXT_PREFILTER_TYPE_RESPONSE_DETAIL')}</div>
                  <div className="w-100 row justify-content-center">
                    <div className="ml-3 col-6 mt-1">
                      <div className="d-flex flex-row mt-2">
                        <DropdownSelect
                          selectedPlaceholder
                          placeholder={t('TEXT_PREFILTER_SELECT_PLACEHOLDER')}
                          options={commonValidators.map((v) => ({ id: v.id, name: v.name }))}
                          selected={question?.validatorType}
                          menuClassnames="dropdown-regex-max-height"
                          setState={(e) => {
                            setCustomValidator(e.id === customRegexId);
                            handleChange(
                              'validatorRegex',
                              0,
                              commonValidators.find((v) => v.id === e.id).regex
                            );
                          }}
                        />
                        <InfoTooltip
                          placement="right"
                          className="mt-3 ml-2"
                          content={
                            <div className="d-flex flex-column p-2">
                              <span className=" text-xs mb-2">
                                {t('TEXT_PREFILTER_TYPE_DETAILS')}
                              </span>
                            </div>
                          }
                        />
                      </div>
                      {customValidator && (
                        <>
                          <input
                            type="text"
                            value={question?.validatorRegex}
                            defaultValue={question?.validatorRegex}
                            onChange={(e) => {
                              try {
                                const validReg = new RegExp(e.target.value);
                                setRegexError(!validReg);
                                handleChange('validatorRegex', 0, e.target.value);
                              } catch {
                                setRegexError(true);
                              }
                            }}
                            placeholder={t('PREFILTER_CUSTOM_REGEX_PLACEHOLDER')}
                            className={`w-100 mt-2 py-2 px-3 border-thin-blue-principal outline-none text-xs ${
                              regexError && 'border-danger'
                            }`}
                          />
                          {regexError && (
                            <a
                              href={t('PREFILTER_CUSTOM_REGEX_HELP_LINK')}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-principal text-xs mt-2 d-block"
                            >
                              {t('WRONG_REGULAR_EXPRESSION')}
                            </a>
                          )}
                        </>
                      )}
                    </div>
                    <input
                      type="number"
                      name="prefilterMaxChar"
                      value={question?.maxChar}
                      onKeyDown={(e) => {
                        const excludedKeys = ['e', '+', '-', '.'];
                        if (excludedKeys.includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => handleChange('maxChar', 0, Number(e.target.value))}
                      className="pb-3 mt-4 bottom-border div-input w-content h-100 b-radius-8 max-char-input text-xs font-weight-400 text-center col-5"
                      min={1}
                      max={500}
                      data-testid="question-text-max-char-input"
                      placeholder={t('CREATE_PREFILTER_MAX_CHAR_ANSWER')}
                    />
                  </div>
                </div>
              )}
              {answerType === AnswerTypeEnum.LOCATIONS && (
                <div className="ml-5 w-90">
                  <div className="w-100 mb-2">
                    {t('MAP_PREFILTER_TYPE_RESPONSE_DETAIL')}
                    <div className="map-locations-max-height">
                      {question?.locations &&
                        question?.locations?.map((l, index) => (
                          <>
                            <div className="d-flex align-items-center px-2 py-2" key={l?.label}>
                              <div className="flex-1 px-2 text-sm font-weight-500 text-start">
                                {t('PREFILTER_QUESTION_ADDRESS_TITLE')} {l?.label}
                              </div>
                              <div className="flex-1 px-2 d-flex align-items-center justify-content-between">
                                <span>{1}</span>
                                <Slider
                                  className="map-range-slider mx-3 flex-grow-1"
                                  min={1}
                                  max={150}
                                  defaultValue={l?.radiusInKm}
                                  onChange={(e) => {
                                    handleChange('locationArea', index, e);
                                  }}
                                  railStyle={{ backgroundColor: '#e0e0e0' }}
                                />
                                <span>{l?.radiusInKm} km. </span>
                                <button
                                  type="button"
                                  data-testid="row-answer-delete-question-button"
                                  onClick={() => handleChange('locations', index, '')}
                                  className="bg-transparent border-0 outline-none ml-2"
                                >
                                  <DeleteForever fill="#4987be" />
                                </button>
                              </div>
                            </div>
                          </>
                        ))}
                    </div>
                    {answerType === AnswerTypeEnum.LOCATIONS && (
                      <AddressSearch
                        markers={question?.locations}
                        handleSetMarkers={(e) => handleChange('locations', -1, e)}
                        limitMarkers={5}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="row my-2 justify-content-center">
              <button
                type="submit"
                onClick={(e) => {
                  if (editQuestion) handleEditQuestion();
                  else handleAddQuestion(e);
                }}
                disabled={disabledAdd}
                className="mt-4 align-self-end px-3 py-1 text-white text-xs font-weight-bold border-0 outline-none bg-maya-blue"
              >
                {editQuestion
                  ? t('CREATE_PRE_FILTER_EDIT_QUESTION_SELECTED')
                  : t('CREATE_PRE_FILTER_ADD_QUESTION_SELECTED')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
